import React, { useState } from "react";
import { toast } from "sonner";
import { Pin, MessageSquareWarning } from "lucide-react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { finApi, finErr, money } from "@/lib/financeApi";
import { Chip } from "@/components/nds/windows/finance/parts";

export const STATUS = {
  paid: ["green", "Paid this week"], to_verify: ["yellow", "To verify"], expected: ["blue", "Expected"],
  missing: ["red", "Missing this week"], not_expected: ["grey", "Not expected"], none: ["grey", "No pay source"],
};
const MODES = [["auto", "Auto"], ["invoiced", "Invoiced"], ["self_logs", "Self-logs"]];
const link = "font-mono text-[10px] uppercase tracking-[0.14em] text-nex-gold hover:underline";
const day = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric" }) : "—");

const PinControl = ({ row, canPin, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const mode = row.pin?.mode || "auto";
  const set = async (m) => {
    if (m === mode) return;
    setBusy(true);
    try { await finApi.post(`/pay-source/pins/${row.resident_id}`, { mode: m }); toast.success(m === "auto" ? "Pin removed — back to Auto" : `Pinned to ${m === "invoiced" ? "Invoiced" : "Self-logs"}`); onChanged(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex rounded-full border border-white/10 overflow-hidden" data-testid={`ps-pin-${row.resident_id}`}>
      {MODES.map(([m, l]) => (
        <button key={m} type="button" disabled={!canPin || busy} onClick={() => set(m)} data-testid={`ps-pin-${m}-${row.resident_id}`} aria-pressed={mode === m}
          title={canPin ? "" : "Only super admins and roles with pin permission can change this"}
          className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] transition-colors disabled:cursor-not-allowed ${mode === m ? "bg-nex-gold/15 text-nex-gold" : "text-nex-muted hover:text-nex-silver"}`}>{l}</button>
      ))}
    </div>
  );
};

const Detail = ({ row }) => {
  if (row.group === "invoiced" && row.invoice_count) {
    const vendors = [...new Set(row.invoices.map((i) => i.vendor_name))].join(", ");
    const next = row.invoices.map((i) => i.pay_date).filter(Boolean).sort().reverse()[0];
    return <span data-testid={`ps-detail-${row.resident_id}`}>{vendors} · {money(row.invoice_total)}{row.invoice_count > 1 ? ` · ${row.invoice_count} invoices` : ""} · pay date {day(next)}</span>;
  }
  const pc = row.last_paycheck;
  if (!pc) return <span data-testid={`ps-detail-${row.resident_id}`}>No paychecks logged yet</span>;
  return <span data-testid={`ps-detail-${row.resident_id}`}>Last check {day(pc.day)} · {money(pc.gross_amount)} · {pc.status === "verified" ? "verified" : "to verify"}</span>;
};

const Remind = ({ row, onDone }) => {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const d = await finApi.post(`/pay-source/${row.resident_id}/remind`, {}); toast.success(`Reminder ${d.sms_status === "sent" ? "texted" : d.sms_status.replace(/_/g, " ")} · ${row.name}`); setAsk(false); onDone(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  if (!ask) return <button type="button" onClick={() => setAsk(true)} data-testid={`ps-remind-${row.resident_id}`} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em] text-red-300 hover:text-red-200"><MessageSquareWarning className="h-3 w-3" /> Remind resident</button>;
  return (
    <span className="inline-flex items-center gap-2 text-[11px] text-nex-silver" data-testid={`ps-remind-confirm-${row.resident_id}`}>
      Text {row.name} a reminder to log this week's paycheck?
      <button type="button" disabled={busy} onClick={send} data-testid={`ps-remind-send-${row.resident_id}`} className="font-mono text-[10px] uppercase text-nex-gold">Send text</button>
      <button type="button" onClick={() => setAsk(false)} data-testid={`ps-remind-cancel-${row.resident_id}`} className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
    </span>
  );
};

export default function PaySourceRow({ row, canPin, canRemind, onChanged }) {
  const { openWindow } = useFloatingWindows();
  const [tone, label] = STATUS[row.status] || STATUS.none;
  const openRes = () => openWindow("campus_residents", { title: "Campus Residents", focus: { residentId: row.resident_id, tab: "financial" } });
  const openIntake = () => openWindow("fin_paychecks", { title: "Paycheck Intake", residentId: row.resident_id, nonce: Date.now() });
  const openInvoice = () => openWindow("fin_invoicing", { title: "Landlord Invoicing", invoiceId: row.invoices[0]?.id, nonce: Date.now() });
  const tip = row.status_note || "";
  return (
    <li className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`ps-row-${row.resident_id}`}>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={openRes} data-testid={`ps-name-${row.resident_id}`} className="text-sm text-nex-silver font-medium hover:text-nex-gold transition-colors">{row.name}</button>
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-nex-muted" data-testid={`ps-group-${row.resident_id}`}>{row.group === "self_logs" ? "Self-logs" : row.group === "invoiced" ? "Invoiced" : "Unassigned"}</span>
          {row.pinned && <span title={`Pinned by ${row.pin?.set_by?.name || "staff"} · ${new Date(row.pin?.set_at).toLocaleString()}`} data-testid={`ps-pinned-${row.resident_id}`} className="inline-flex items-center gap-0.5 font-mono text-[9px] uppercase text-nex-gold"><Pin className="h-2.5 w-2.5" /> Pinned</span>}
          {row.flags.includes("has_open_invoice") && <span data-testid={`ps-flag-open-invoice-${row.resident_id}`} className="font-mono text-[9px] uppercase text-amber-300">Has open invoice</span>}
          {row.flags.includes("no_invoice") && <span data-testid={`ps-flag-no-invoice-${row.resident_id}`} className="font-mono text-[9px] uppercase text-amber-300">No invoice yet</span>}
        </div>
        <div className="text-xs text-nex-muted"><Detail row={row} /></div>
      </div>
      <button type="button" title={tip} onClick={row.week_paycheck ? openIntake : undefined} data-testid={`ps-status-${row.resident_id}`} className={row.week_paycheck ? "cursor-pointer" : "cursor-default"}>
        <Chip tone={tone}>{label}</Chip>
      </button>
      <div className="flex items-center gap-3">
        {row.group === "invoiced" && row.invoice_count > 0 && <button type="button" onClick={openInvoice} data-testid={`ps-open-invoice-${row.resident_id}`} className={link}>Open invoice</button>}
        {row.group !== "invoiced" && <button type="button" onClick={openIntake} data-testid={`ps-open-intake-${row.resident_id}`} className={link}>Open intake</button>}
        {row.status === "missing" && canRemind && <Remind row={row} onDone={onChanged} />}
        <PinControl row={row} canPin={canPin} onChanged={onChanged} />
      </div>
    </li>
  );
}
